import AppButton from "components/AppButton/AppButton";
import ContainerFlex, { PrintableBodyWrapper } from "components/ContainerFlex";
import useApi, { ApiMethod } from "hooks/useApi";
import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";
import ReactToPrint from "react-to-print";
import { useAppDispatch, useAppSelector } from "redux/app/hooks";
import { setLoading, setTitleLoading } from "redux/ui/loadings-slice";
import { RouteNames } from "routes/routes";
import Endpoints from "services/api/endpoints";
import { isBlockedCard } from "utils/clientStatus";
import { cpfUnmask, hiddenCardNumber, moneyMask } from "utils/mask";
import { showMessage } from "utils/messages";
import { getItemLocalStore, saveItemLocalStore } from "utils/storeUtils";
import {
  TIPO_CARTAO_CONVENIO,
  TIPO_CARTAO_TEUCARD,
  TipoCartao,
} from "models/tipoCartao";
import {
  LOCAL_STORE_CLIENTE,
  LOCAL_STORE_THEME_IMAGE,
} from "../../../constants";
import { CardLimiteResumo } from "./CardLimiteResumo/CardLimiteResumo";
import "./Limite.css";
import * as St from "./styles";

const Limite = () => {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const {
    cliente: { cliente },
  } = useAppSelector((state) => state);

  const diaAtual = moment().format("DD/MM/YYYY");
  const componentRef = useRef<HTMLDivElement>(null);
  const [dadosTeuCard, setDadosTeuCard] = useState<any>(null);
  const [dadosConvenio, setDadosConvenio] = useState<any>(null);
  const [cardImageUrl, setCardImageUrl] = useState<string | null>(null);

  const { statusCliente, cartaoAtual, cartaoAtualConvenio, dadosLogin } =
    useMemo(() => getItemLocalStore(LOCAL_STORE_CLIENTE), []);

  const cartaoAtualCompleto = useMemo(
    () =>
      cartaoAtual && {
        ...cartaoAtual,
        numeroCartao: dadosLogin?.numeroCartaoTeucard,
        nomeTitular: dadosLogin?.nomeTitular,
        temaDoCartao: dadosLogin?.modeloTemaCartao,
      },
    [cartaoAtual, dadosLogin],
  );
  const cartaoAtualConvenioCompleto = useMemo(
    () =>
      cartaoAtualConvenio && {
        ...cartaoAtualConvenio,
        numeroCartao: dadosLogin?.numeroCartaoConvenio,
        nomeTitular: dadosLogin?.nomeTitular,
      },
    [cartaoAtualConvenio, dadosLogin],
  );

  const teucardBloqueado = isBlockedCard(cartaoAtual?.status);
  const convenioBloqueado = isBlockedCard(cartaoAtualConvenio?.status);
  const availableCards = useMemo(() => {
    const cards: { tipo: TipoCartao; cartao: any }[] = [];

    if (cartaoAtualCompleto && !teucardBloqueado) {
      cards.push({ tipo: TIPO_CARTAO_TEUCARD, cartao: cartaoAtualCompleto });
    }

    if (cartaoAtualConvenioCompleto && !convenioBloqueado) {
      cards.push({
        tipo: TIPO_CARTAO_CONVENIO,
        cartao: cartaoAtualConvenioCompleto,
      });
    }

    return cards;
  }, [
    cartaoAtualCompleto,
    cartaoAtualConvenioCompleto,
    teucardBloqueado,
    convenioBloqueado,
  ]);

  const shouldShowTabs = availableCards.length === 2;
  const [activeTab, setActiveTab] = useState<TipoCartao | null>(
    () => availableCards[0]?.tipo ?? null,
  );
  const { typeFlow } = useAppSelector((state) => state.security);

  const getTokenCartaoByNumero = useApi<any>(
    Endpoints.autorizador.getTokenNumeroCartao,
    ApiMethod.POST,
    {},
    {},
    { usePublicToken: true },
  );

  const getSaldoClienteTokenCartao = useApi<any>(
    Endpoints.autorizador.getSaldoClienteByTokenCartao,
    ApiMethod.POST,
  );

  useEffect(() => {
    if (!availableCards.length) {
      history.push(RouteNames.HOME);
      return;
    }

    if (!activeTab || !availableCards.some((card) => card.tipo === activeTab)) {
      setActiveTab(availableCards[0].tipo);
    }
  }, [availableCards, activeTab, history]);
  const getCardDetailsTeuCard = useApi<any>(
    Endpoints.usuario.getLastCardData
      .replace("PARAM_CPF", cpfUnmask(cliente.cpf) || "")
      .split("?")[0],
    ApiMethod.GET,
  );

  const getCardDetailsConvenio = useApi<any>(
    Endpoints.usuario.getLastCardData
      .replace("PARAM_CPF", cpfUnmask(cliente.cpf) || "")
      .replace("PARAM_TIPO", "CONVENIO"),
    ApiMethod.GET,
  );

  const getLimiteClienteConvenio = useCallback(async () => {
    dispatch(setLoading(true));
    dispatch(setTitleLoading("Carregando dados do seu cartão..."));

    const tokenCartao = await getTokenCartaoByNumero.callApi({
      numeroCartao: dadosLogin?.numeroCartaoConvenio,
    });
    if (tokenCartao.status >= 400) {
      dispatch(setLoading(false));
      const { mensagem } = tokenCartao.data;
      showMessage("warning", "Atenção!", mensagem);
      history.push(RouteNames.HOME);
    }

    const responseSaldo = await getSaldoClienteTokenCartao.callApi({
      tokenCartao: tokenCartao.data.tokenCartao,
    });

    if (responseSaldo.status >= 400) {
      dispatch(setLoading(false));
      const { mensagem } = responseSaldo.data;
      showMessage("warning", "Atenção!", mensagem);
      history.push(RouteNames.HOME);
    }

    setDadosConvenio({
      saldo: responseSaldo.data,
      melhorDia: Number(responseSaldo.data?.melhorDia?.slice(0, 2)) ?? 0,
      usabilidadeAtivaModalidades:
        responseSaldo.data?.usabilidadeAtivaModalidades,
    });

    dispatch(setLoading(false));
  }, [
    dadosLogin,
    dispatch,
    getSaldoClienteTokenCartao,
    getTokenCartaoByNumero,
    history,
  ]);

  const getLimiteCliente = useCallback(async () => {
    dispatch(setLoading(true));
    dispatch(setTitleLoading("Carregando dados do seu cartão..."));

    // const response = await fetchFaturaCliente.callApi({
    //   numeroCartao: dadosLogin?.numeroCartaoTeucard,
    // });

    const tokenCartao = await getTokenCartaoByNumero.callApi({
      numeroCartao: dadosLogin?.numeroCartaoTeucard,
    });
    if (tokenCartao.status >= 400) {
      dispatch(setLoading(false));
      const { mensagem } = tokenCartao.data;
      showMessage("warning", "Atenção!", mensagem);
      history.push(RouteNames.HOME);
    }

    const responseSaldo = await getSaldoClienteTokenCartao.callApi({
      tokenCartao: tokenCartao.data.tokenCartao,
    });

    if (responseSaldo.status >= 400) {
      dispatch(setLoading(false));
      const { mensagem } = responseSaldo.data;
      showMessage("warning", "Atenção!", mensagem);
      history.push(RouteNames.HOME);
    }

    setDadosTeuCard({
      saldo: responseSaldo.data,
      melhorDia: Number(responseSaldo.data?.melhorDia?.slice(0, 2)) ?? 0,
      usabilidadeAtivaModalidades:
        responseSaldo.data?.usabilidadeAtivaModalidades,
    });

    dispatch(setLoading(false));
  }, [
    dadosLogin,
    dispatch,
    getSaldoClienteTokenCartao,
    getTokenCartaoByNumero,
    history,
  ]);

  useEffect(() => {
    if (activeTab === "TEUCARD" && !dadosTeuCard) getLimiteCliente();
    if (activeTab === "CONVENIO" && !dadosConvenio) getLimiteClienteConvenio();
    // getLimiteCliente/getLimiteClienteConvenio carregam deps do useApi (nao
    // memoizadas de verdade) — incluir causaria refetch em loop a cada render.
    // dadosTeuCard/dadosConvenio entram pois o proprio guard (!dadosTeuCard/
    // !dadosConvenio) impede refetch repetido assim que populados.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, dadosTeuCard, dadosConvenio]);

  const fetchCardImage = useCallback(async () => {
    dispatch(setLoading(true));
    dispatch(setTitleLoading("Carregando dados do seu cartão..."));

    if (!activeTab) return;

    const cardDetailsApi =
      activeTab === "TEUCARD" ? getCardDetailsTeuCard : getCardDetailsConvenio;

    const response = await cardDetailsApi.callApi();

    if (response.status < 400 && response.data) {
      setCardImageUrl(response.data?.linkImagemTema);
      saveItemLocalStore(
        LOCAL_STORE_THEME_IMAGE,
        response.data?.linkImagemTema,
      );
    }

    dispatch(setLoading(false));
  }, [activeTab, dispatch, getCardDetailsConvenio, getCardDetailsTeuCard]);

  useEffect(() => {
    fetchCardImage();
    // fetchCardImage carrega deps do useApi (nao memoizadas de verdade); incluir
    // causaria refetch em loop a cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  return (
    <>
      <div id="limite-top-texts">
        <span id="limite-title">Limite:</span>

        <span id="limite-description">
          Bem-vindo à tua tela de Limite{" "}
          {activeTab === "TEUCARD" ? "TeuCard" : "Convênio"}
        </span>

        {shouldShowTabs ? (
          <Tabs
            selectedIndex={activeTab === "TEUCARD" ? 0 : 1}
            onSelect={(index: number) =>
              setActiveTab(index === 0 ? "TEUCARD" : "CONVENIO")
            }
          >
            <TabList>
              <Tab>TEUCARD</Tab>
              <Tab>CONVÊNIO</Tab>
            </TabList>

            <TabPanel>
              <CardLimiteResumo
                tipoCartao="TEUCARD"
                cartao={cartaoAtualCompleto}
                dados={dadosTeuCard}
                cardImageUrl={cardImageUrl}
                statusCliente={statusCliente}
              />{" "}
            </TabPanel>

            <TabPanel>
              <CardLimiteResumo
                tipoCartao="CONVENIO"
                cartao={cartaoAtualConvenioCompleto}
                dados={dadosConvenio}
                cardImageUrl={cardImageUrl}
                statusCliente={statusCliente}
              />{" "}
            </TabPanel>
          </Tabs>
        ) : (
          activeTab && (
            <CardLimiteResumo
              tipoCartao={activeTab}
              cartao={
                activeTab === "TEUCARD"
                  ? cartaoAtualCompleto
                  : cartaoAtualConvenioCompleto
              }
              dados={activeTab === "TEUCARD" ? dadosTeuCard : dadosConvenio}
              cardImageUrl={cardImageUrl}
              statusCliente={statusCliente}
            />
          )
        )}

        <ContainerFlex
          id="limite-btn-voltar"
          marginTop={10}
          center
          horizontal
          row
          space
        >
          <AppButton
            content="Voltar"
            route={
              typeFlow === "limite"
                ? RouteNames.HOME
                : RouteNames.ATENDIMENTO_HOME
            }
            outlined={true}
            style={{ width: "280px" }}
            click={() => {}}
          />
          <ReactToPrint
            trigger={() => <button className="normal">Imprimir</button>}
            content={() => componentRef.current}
          />
        </ContainerFlex>
      </div>
      <ContainerFlex center padding={5} disabled>
        <ContainerFlex
          padding={15}
          color="#fbfbfb"
          width={45}
          height={55}
          maxWidth={45}
          maxHeight={400}
          scroll
        >
          {activeTab === "TEUCARD" ? (
            <PrintableBodyWrapper ref={componentRef}>
              <ContainerFlex row>
                <St.TitleFaturaTextValue>
                  Resumo de Limite TeuCard
                </St.TitleFaturaTextValue>
                <St.DateFaturaTextValue>Data {diaAtual}</St.DateFaturaTextValue>
              </ContainerFlex>
              <ContainerFlex row>
                <ContainerFlex row>
                  <St.SpanTextTitleBold>Cliente: </St.SpanTextTitleBold>
                </ContainerFlex>
                <ContainerFlex row end align="flex-end">
                  <St.SpanTextTitleBold>Número de Cartão:</St.SpanTextTitleBold>
                </ContainerFlex>
              </ContainerFlex>
              <ContainerFlex row>
                <ContainerFlex row>
                  <St.SpanTextTitle>
                    {cartaoAtual && cartaoAtualCompleto?.nomeTitular}
                  </St.SpanTextTitle>
                </ContainerFlex>
                <ContainerFlex end align="flex-end">
                  <St.SpanTextTitle>
                    {cartaoAtual &&
                      hiddenCardNumber(cartaoAtualCompleto?.numeroCartao)}
                  </St.SpanTextTitle>
                </ContainerFlex>
              </ContainerFlex>
              <St.Dividers />
              <St.SpanTextTitleBold>Limite de Crédito R$</St.SpanTextTitleBold>
              <ContainerFlex row>
                <St.SpanTextTitle>Limite total de crédito</St.SpanTextTitle>
                <ContainerFlex end align="flex-end">
                  <St.SpanTextValue>
                    {moneyMask(dadosTeuCard?.saldo?.limiteCartao)}
                  </St.SpanTextValue>
                </ContainerFlex>
              </ContainerFlex>
              <ContainerFlex row>
                <St.SpanTextTitleBold>
                  Limite disponível R$
                </St.SpanTextTitleBold>
                <ContainerFlex end align="flex-end">
                  <St.SpanTextValue>
                    {dadosTeuCard?.saldo && statusCliente !== "INADIMPLENTE"
                      ? moneyMask(dadosTeuCard?.saldo?.saldo)
                      : 0}
                  </St.SpanTextValue>
                </ContainerFlex>
              </ContainerFlex>
              <ContainerFlex row>
                <St.SpanTextTitleBold>
                  Melhor dia de compra
                </St.SpanTextTitleBold>
                <ContainerFlex end align="flex-end">
                  <St.SpanTextValue>
                    {dadosTeuCard?.melhorDia ?? 0}
                  </St.SpanTextValue>
                </ContainerFlex>
              </ContainerFlex>
              <St.Dividers />
            </PrintableBodyWrapper>
          ) : (
            <PrintableBodyWrapper ref={componentRef}>
              <ContainerFlex row>
                <St.TitleFaturaTextValue>
                  Resumo de Limite Convênio
                </St.TitleFaturaTextValue>
                <St.DateFaturaTextValue>Data {diaAtual}</St.DateFaturaTextValue>
              </ContainerFlex>
              <ContainerFlex row>
                <ContainerFlex row>
                  <St.SpanTextTitleBold>Cliente: </St.SpanTextTitleBold>
                </ContainerFlex>
                <ContainerFlex row end align="flex-end">
                  <St.SpanTextTitleBold>Número de Cartão:</St.SpanTextTitleBold>
                </ContainerFlex>
              </ContainerFlex>
              <ContainerFlex row>
                <ContainerFlex row>
                  <St.SpanTextTitle>
                    {cartaoAtualConvenio &&
                      cartaoAtualConvenioCompleto?.nomeTitular}
                  </St.SpanTextTitle>
                </ContainerFlex>
                <ContainerFlex end align="flex-end">
                  <St.SpanTextTitle>
                    {cartaoAtualConvenio &&
                      hiddenCardNumber(
                        cartaoAtualConvenioCompleto?.numeroCartao,
                      )}
                  </St.SpanTextTitle>
                </ContainerFlex>
              </ContainerFlex>
              <St.Dividers />
              <St.SpanTextTitleBold>Convênio</St.SpanTextTitleBold>
              <ContainerFlex row>
                <St.SpanTextTitle>Limite Total</St.SpanTextTitle>
                <ContainerFlex end align="flex-end">
                  <St.SpanTextValue>
                    {moneyMask(dadosConvenio?.saldo?.limiteCartao ?? 0)}
                  </St.SpanTextValue>
                </ContainerFlex>
              </ContainerFlex>
              <ContainerFlex row>
                <St.SpanTextTitle>Limite Disponível</St.SpanTextTitle>
                <ContainerFlex end align="flex-end">
                  <St.SpanTextValue>
                    {dadosConvenio?.saldo && statusCliente !== "INADIMPLENTE"
                      ? moneyMask(dadosConvenio?.saldo?.saldo ?? 0)
                      : 0}
                  </St.SpanTextValue>
                </ContainerFlex>
              </ContainerFlex>
              <St.Dividers />
              <St.SpanTextTitleBold>Vale-compras</St.SpanTextTitleBold>
              <ContainerFlex row>
                <St.SpanTextTitle>Limite Total</St.SpanTextTitle>
                <ContainerFlex end align="flex-end">
                  <St.SpanTextValue>
                    {moneyMask(dadosConvenio?.saldo?.limiteCesta ?? 0)}
                  </St.SpanTextValue>
                </ContainerFlex>
              </ContainerFlex>
              <ContainerFlex row>
                <St.SpanTextTitle>Limite Disponível</St.SpanTextTitle>
                <ContainerFlex end align="flex-end">
                  <St.SpanTextValue>
                    {dadosConvenio?.saldo && statusCliente !== "INADIMPLENTE"
                      ? moneyMask(
                          dadosConvenio?.saldo?.limiteCestaDisponivel ?? 0,
                        )
                      : 0}
                  </St.SpanTextValue>
                </ContainerFlex>
              </ContainerFlex>
              <St.Dividers />
            </PrintableBodyWrapper>
          )}
        </ContainerFlex>
      </ContainerFlex>
    </>
  );
};

export default Limite;
